import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Button,
  Box,
  TextField,
  Paper,
  Divider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DrawIcon from "@mui/icons-material/Draw";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import dayjs from "dayjs";

import CommonSelect from "../common/CommonSelect";
import {
  fetchAssessmentReferenceData,
  fetchDraftPayloadApi,
  initializeAssessmentApi,
  submitAssessmentApi,
  getOutcomeNameFromReferenceData,
  isDeclinedOutcome,
} from "../../api/assessment";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import { useLoading } from "../../context/LoadingContext";

const getNameFromPayload = (payload) =>
  payload.fullName ||
  `${payload.firstName || ""} ${payload.lastName || ""}`.trim() ||
  payload.displayName ||
  payload.name ||
  payload.username ||
  "";

const getLoggedInUserName = () => {
  try {
    const user = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const name = getNameFromPayload(user);
    if (name) return name;
  } catch (e) {
    // ignore
  }

  try {
    const token =
      localStorage.getItem("token") ||
      localStorage.getItem("access_token") ||
      "";
    if (token?.includes(".")) {
      const parts = token.split(".");
      if (parts.length >= 2) {
        const payload = JSON.parse(
          atob(parts[1].replaceAll("-", "+").replaceAll("_", "/")),
        );
        const tokenName = getNameFromPayload(payload);
        if (tokenName) return tokenName;
      }
    }
  } catch (e) {
    // ignore
  }
  return "";
};

const formatSigneeName = (nameStr) => {
  const trimmed = String(nameStr || "").trim();
  if (!trimmed || trimmed === "N/A" || trimmed === "—") {
    return "";
  }
  if (/\b(RN|NP|MD|DO|PA|LPN)\b/i.test(trimmed)) {
    return trimmed;
  }
  return `${trimmed}, RN`;
};

const checkBiosimilar = (val) => {
  if (!val) return false;
  if (typeof val === "string") return val.toUpperCase().includes("BIOSIMILAR");
  if (typeof val === "object") {
    return (
      checkBiosimilar(val.programId) ||
      checkBiosimilar(val.programName) ||
      checkBiosimilar(val.name) ||
      checkBiosimilar(val.program) ||
      checkBiosimilar(val.id) ||
      checkBiosimilar(val.canonicalId)
    );
  }
  return false;
};

const getProgsList = (noteData) => {
  if (Array.isArray(noteData.programOutcomes)) {
    return noteData.programOutcomes;
  }
  if (Array.isArray(noteData.programs)) {
    return noteData.programs;
  }
  if (Array.isArray(noteData.payload?.programOutcomes)) {
    return noteData.payload.programOutcomes;
  }
  if (Array.isArray(noteData.payload?.programs)) {
    return noteData.payload.programs;
  }
  return [];
};

const findBiosimilarProgram = (noteData) => {
  const progsList = getProgsList(noteData);

  let bioProg = null;
  if (progsList.length > 0) {
    bioProg = progsList.find((p) => checkBiosimilar(p));
    if (!bioProg && progsList.length === 1) {
      bioProg = progsList[0];
    }
  }
  if (!bioProg && Array.isArray(noteData.programs)) {
    bioProg = noteData.programs.find((p) => checkBiosimilar(p));
  }
  return bioProg;
};

const extractBiosimilarNoteFields = (noteData) => {
  const bioProg = findBiosimilarProgram(noteData);

  const isRootBiosimilar = checkBiosimilar(
    noteData.programId || noteData.programName || noteData.program,
  );

  const outcomeVal =
    bioProg?.outcome ||
    (isRootBiosimilar ? noteData.outcome : "") ||
    noteData.outcome ||
    "";
  const subReasonVal =
    bioProg?.subReason ||
    bioProg?.sub_reason ||
    (isRootBiosimilar ? noteData.subReason || noteData.sub_reason : "") ||
    noteData.subReason ||
    noteData.sub_reason ||
    "";
  const reasonVal =
    bioProg?.reason ||
    bioProg?.outcomeReason ||
    bioProg?.outcome_reason ||
    bioProg?.declinedReason ||
    bioProg?.declined_reason ||
    (isRootBiosimilar
      ? noteData.reason ||
        noteData.outcomeReason ||
        noteData.outcome_reason ||
        noteData.declinedReason ||
        noteData.declined_reason
      : "") ||
    noteData.reason ||
    noteData.outcomeReason ||
    noteData.outcome_reason ||
    noteData.declinedReason ||
    noteData.declined_reason ||
    "";
  const decNotesVal =
    bioProg?.declinedNote ||
    bioProg?.declinedNotes ||
    bioProg?.declined_note ||
    bioProg?.declined_notes ||
    (isRootBiosimilar
      ? noteData.declinedNote ||
        noteData.declinedNotes ||
        noteData.declined_note ||
        noteData.declined_notes
      : "") ||
    noteData.declinedNote ||
    noteData.declinedNotes ||
    noteData.declined_note ||
    noteData.declined_notes ||
    "";

  return { outcomeVal, subReasonVal, reasonVal, decNotesVal };
};

const getRawAssignedRn = (note, assigneeRnName, memberData) =>
  note?.assigneeRnName ||
  note?.assignee_rn_name ||
  note?.assignedRn ||
  note?.assigned_rn ||
  note?.assignedRnName ||
  note?.payload?.assigneeRnName ||
  note?.payload?._outcomes?.assigneeRnName ||
  assigneeRnName ||
  memberData?.assignedRn ||
  memberData?.assigned_rn ||
  memberData?.assignedRnName ||
  memberData?.assigned_rn_name ||
  memberData?.assigneeRnName ||
  memberData?.assignee_rn_name ||
  memberData?.member?.assignedRn ||
  memberData?.member?.assigneeRnName ||
  memberData?.assigneeName ||
  memberData?.assignee?.name ||
  note?.submittedBy ||
  note?.submittedByName;

const resolveDisplaySigneeName = (
  note,
  readOnly,
  assigneeRnName,
  memberData,
) => {
  if (!readOnly) {
    const loggedIn = getLoggedInUserName();
    if (loggedIn && loggedIn !== "N/A" && loggedIn !== "—") {
      return formatSigneeName(loggedIn);
    }
  }

  const rawAssignedRn = getRawAssignedRn(note, assigneeRnName, memberData);

  if (rawAssignedRn) {
    const formatted = formatSigneeName(rawAssignedRn);
    if (formatted) return formatted;
  }

  const fallbackLoggedIn = getLoggedInUserName();
  return formatSigneeName(fallbackLoggedIn);
};

const isOutcomeDeclined = (outcome, referenceData) =>
  Boolean(
    isDeclinedOutcome(outcome, referenceData) ||
    outcome === "Declined" ||
    String(outcome || "")
      .toLowerCase()
      .includes("decline"),
  );

const resolveOutcomeName = (referenceData, displayName) => {
  const matched = referenceData?.outcomes?.find(
    (o) => o.displayName === displayName,
  );
  return matched ? matched.outcomeName : displayName;
};

const resolveOutcomeDisplayName = (outcomeVal, referenceData) => {
  if (!outcomeVal) return "";
  const matched = referenceData?.outcomes?.find(
    (o) =>
      o.outcomeName === outcomeVal ||
      o.displayName === outcomeVal ||
      String(o.outcomeName).toUpperCase() ===
        String(outcomeVal).toUpperCase() ||
      String(o.displayName).toUpperCase() === String(outcomeVal).toUpperCase(),
  );
  if (matched) return matched.displayName;

  const norm = String(outcomeVal).toLowerCase().replaceAll("_", " ");
  return capitalizeFirstLetter(norm);
};

const buildOutcomesList = (referenceData) => {
  if (!referenceData?.outcomes || referenceData.outcomes.length === 0) {
    return ["Enrolled", "Completed", "Declined", "Discharged"];
  }
  const names = [];
  referenceData.outcomes.forEach((o) => {
    if (o.displayName && !names.includes(o.displayName)) {
      names.push(o.displayName);
    }
  });
  return names;
};

const buildDynamicOutcomeReasons = (referenceData) => {
  const reasons = {};
  const outcomes = referenceData?.outcomes || [];
  const memberDeclinedObj = outcomes.find(
    (o) =>
      o.outcomeName === "MEMBER_DECLINED" || o.outcome === "MEMBER_DECLINED",
  );
  const declinedObj = outcomes.find(
    (o) => o.outcomeName === "DECLINED" || o.outcome === "DECLINED",
  );

  outcomes.forEach((o) => {
    if (o.subReasons && o.subReasons.length > 0) {
      reasons[o.displayName] = o.subReasons.map((s) => s.displayName);
    }
  });

  if (memberDeclinedObj?.subReasons?.length > 0) {
    const targetKey =
      memberDeclinedObj.displayName || declinedObj?.displayName || "Declined";
    reasons[targetKey] = memberDeclinedObj.subReasons.map((s) => s.displayName);
  }

  return reasons;
};

const loadNoteData = async (existingNote) => {
  let noteData = existingNote;
  const targetId = existingNote.batchId || existingNote.id;
  if (targetId) {
    try {
      const fetched = await fetchDraftPayloadApi(targetId, {
        isBatch: true,
      });
      if (fetched) {
        noteData = { ...existingNote, ...fetched };
      }
    } catch (err) {
      console.error(
        "Failed to fetch full assessment payload for Biosimilars note:",
        err,
      );
    }
  }
  return noteData;
};

const getOutcomeSelectSx = (readOnly, borderColor, primaryColor) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: "40px",
  px: 1.5,
  borderRadius: "8px",
  border: `1px solid ${borderColor}`,
  backgroundColor: readOnly ? "#F3F4F6" : "#fff",
  cursor: readOnly ? "not-allowed" : "pointer",
  "&:hover": {
    borderColor: readOnly ? borderColor : primaryColor,
  },
});

const getNotesFieldRootSx = (readOnly, borderColor) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: readOnly ? "#F3F4F6" : "#fff",
    "& fieldset": { borderColor: borderColor },
  },
});

const getCertificationCheckboxSx = (invalid, borderColor, primaryColor) => ({
  color: invalid ? "#D32F2F !important" : `${borderColor} !important`,
  "& .MuiSvgIcon-root": {
    color: invalid ? "#D32F2F !important" : undefined,
    fill: invalid ? "#D32F2F !important" : undefined,
  },
  "&.Mui-checked": { color: `${primaryColor} !important` },
  "&.Mui-checked .MuiSvgIcon-root": {
    color: `${primaryColor} !important`,
    fill: `${primaryColor} !important`,
  },
  "&:hover": {
    backgroundColor: invalid ? "rgba(211, 47, 47, 0.08) !important" : undefined,
  },
});

const OutcomeLabel = ({ displayName, subReason }) => (
  <>
    {displayName}
    {subReason && (
      <span
        style={{
          color: "#403C6F",
          fontSize: "12px",
          fontWeight: 400,
          marginLeft: 4,
        }}
      >
        ({capitalizeFirstLetter(subReason)})
      </span>
    )}
  </>
);

OutcomeLabel.propTypes = {
  displayName: PropTypes.string,
  subReason: PropTypes.string,
};

const BiosimilarsOutcomeModal = ({
  open,
  onClose,
  onSuccess,
  memberId,
  readOnly = false,
  existingNote = null,
  memberData = null,
  assigneeRnName = "",
}) => {
  const { showLoading, hideLoading } = useLoading();
  const primaryColor = "#403C6F";
  const secondaryColor = "#7F7C9F";
  const borderColor = "#E8E8E9";

  const [outcomeAnchor, setOutcomeAnchor] = useState(null);
  const [referenceData, setReferenceData] = useState(null);

  const [selectedOutcome, setSelectedOutcome] = useState("");
  const [selectedSubReason, setSelectedSubReason] = useState("");
  const [declinedReason, setDeclinedReason] = useState("");
  const [declinedNotes, setDeclinedNotes] = useState("");
  const [clinicalNotes, setClinicalNotes] = useState("");
  const [certification, setCertification] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectRef = React.useRef(null);

  const getDisplaySigneeName = (note) =>
    resolveDisplaySigneeName(note, readOnly, assigneeRnName, memberData);

  const displaySigneeName = getDisplaySigneeName(existingNote);

  const [currentTime, setCurrentTime] = useState(
    dayjs().format("MM/DD/YYYY hh:mm A"),
  );

  useEffect(() => {
    if (readOnly && existingNote) {
      const timeVal =
        existingNote.completionTime ||
        existingNote.startTime ||
        existingNote.timestamp;
      if (timeVal) {
        const fixed = String(timeVal).endsWith("Z")
          ? String(timeVal)
          : String(timeVal) + "Z";
        setCurrentTime(dayjs(fixed).format("MM/DD/YYYY hh:mm A"));
      } else {
        setCurrentTime("—");
      }
      return;
    }
    const timer = setInterval(() => {
      setCurrentTime(dayjs().format("MM/DD/YYYY hh:mm A"));
    }, 1000);
    return () => clearInterval(timer);
  }, [readOnly, existingNote]);

  useEffect(() => {
    const getRefData = async () => {
      try {
        const data = await fetchAssessmentReferenceData();
        setReferenceData(data);
      } catch (error) {
        console.error("Failed to fetch assessment reference data:", error);
      }
    };
    if (open) {
      void getRefData();
    }
  }, [open]);

  useEffect(() => {
    const loadNoteDetails = async () => {
      if (!open) return;

      if (existingNote) {
        showLoading();
        const noteData = await loadNoteData(existingNote);
        hideLoading();

        const { outcomeVal, subReasonVal, reasonVal, decNotesVal } =
          extractBiosimilarNoteFields(noteData);

        setSelectedOutcome(outcomeVal);
        setSelectedSubReason(subReasonVal);
        setDeclinedReason(reasonVal);
        setDeclinedNotes(decNotesVal);
        setClinicalNotes(
          noteData.clinicalNote ||
            noteData.clinicalNotes ||
            noteData.notes ||
            noteData.note ||
            "",
        );
        setCertification(true);
      } else {
        setSelectedOutcome("");
        setSelectedSubReason("");
        setDeclinedReason("");
        setDeclinedNotes("");
        setClinicalNotes("");
        setCertification(false);
      }
    };

    void loadNoteDetails();
  }, [open, existingNote, hideLoading, showLoading]);

  const getOutcomeDisplayName = (outcomeVal) =>
    resolveOutcomeDisplayName(outcomeVal, referenceData);

  const outcomesList = React.useMemo(
    () => buildOutcomesList(referenceData),
    [referenceData],
  );

  const dynamicOutcomeReasons = React.useMemo(
    () => buildDynamicOutcomeReasons(referenceData),
    [referenceData],
  );

  const isDeclined = isOutcomeDeclined(selectedOutcome, referenceData);

  const handleSubmit = async () => {
    if (!certification || !selectedOutcome || !memberId) return;
    showLoading();
    setIsSubmitting(true);
    try {
      const outcomeStr =
        getOutcomeNameFromReferenceData(selectedOutcome, referenceData) ||
        selectedOutcome;

      const initPayload = {
        memberId: Number.parseInt(memberId, 10),
        encounterId: null,
        programs: [
          {
            programId: "BIOSIMILARS",
            formType: "N/A",
          },
        ],
      };

      const sessionRes = await initializeAssessmentApi(initPayload);
      const assessmentId = sessionRes?.id;

      if (!assessmentId) {
        throw new Error("Failed to initialize Biosimilars outcome session.");
      }

      const finalPayload = {
        version: sessionRes.version || 0,
        signatureConfirmed: true,
        finalPayload: {
          assigneeRnName: displaySigneeName,
          signeeName: displaySigneeName,
        },
        assigneeRnName: displaySigneeName,
        programs: [
          {
            programId: "BIOSIMILARS",
            formType: "N/A",
            outcomeName: outcomeStr,
            outcome: outcomeStr,
            subReason: selectedSubReason || null,
            reason: isDeclined ? declinedReason || null : null,
            declinedNote: isDeclined ? declinedNotes || null : null,
          },
        ],
        clinicalNote: clinicalNotes,
        elapsedTime: 0,
      };

      await submitAssessmentApi(assessmentId, finalPayload);
      if (onSuccess) {
        await onSuccess();
      }
      onClose();
    } catch (error) {
      console.error("Error submitting Biosimilars outcome:", error);
    } finally {
      setIsSubmitting(false);
      hideLoading();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            p: 1.5,
            backgroundColor: "#FFFFFF",
          },
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#10100F" }}>
          {readOnly
            ? "View Clinical Outcome - Biosimilars"
            : "Clinical Program Outcome"}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ color: "#7F7C9F" }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2, pb: 2, pt: 0 }}>
        <Box
          component="fieldset"
          disabled={readOnly}
          sx={{ border: "none", p: 0, m: 0 }}
        >
          {/* Program Card */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              border: `1px solid ${borderColor}`,
              borderRadius: "10px",
              backgroundColor: "#FAFAFD",
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={700}
              sx={{ color: primaryColor, mb: 2 }}
            >
              Biosimilars
            </Typography>

            <Box sx={{ display: "flex", gap: 2, flexDirection: "column" }}>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {/* Program Outcome Selection */}
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#403C6F",
                      fontWeight: 500,
                      mb: 1,
                      display: "block",
                    }}
                  >
                    Program Outcome{" "}
                    {!readOnly && <span style={{ color: "#D32F2F" }}>*</span>}
                  </Typography>
                  <Box
                    ref={selectRef}
                    onClick={
                      readOnly ? null : (e) => setOutcomeAnchor(e.currentTarget)
                    }
                    sx={getOutcomeSelectSx(readOnly, borderColor, primaryColor)}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: selectedOutcome ? "#10100F" : "#9F9DB6",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {selectedOutcome ? (
                        <OutcomeLabel
                          displayName={getOutcomeDisplayName(selectedOutcome)}
                          subReason={selectedSubReason}
                        />
                      ) : (
                        "Select program Outcome..."
                      )}
                    </Typography>
                    <KeyboardArrowDownIcon
                      sx={{ color: "#6B7280", fontSize: "20px" }}
                    />
                  </Box>
                  <CommonSelect
                    id="biosimilars-outcome-select"
                    open={Boolean(outcomeAnchor)}
                    anchorEl={outcomeAnchor}
                    onClose={() => setOutcomeAnchor(null)}
                    noteTypes={outcomesList}
                    selectedTypes={
                      selectedOutcome
                        ? [getOutcomeDisplayName(selectedOutcome)]
                        : []
                    }
                    multiple={false}
                    nestedOptions={dynamicOutcomeReasons}
                    nestedSelectedValue={selectedSubReason || ""}
                    nestedVertical="top"
                    onNestedSelect={(parent, child) => {
                      const outcomeName = resolveOutcomeName(
                        referenceData,
                        parent,
                      );
                      setSelectedOutcome(outcomeName);
                      setSelectedSubReason(child);
                      if (!isOutcomeDeclined(outcomeName, referenceData)) {
                        setDeclinedReason("");
                        setDeclinedNotes("");
                      }
                      setOutcomeAnchor(null);
                    }}
                    handleTypeToggle={(type) => {
                      const outcomeName = resolveOutcomeName(
                        referenceData,
                        type,
                      );
                      setSelectedOutcome(outcomeName);
                      setSelectedSubReason("");
                      if (!isOutcomeDeclined(outcomeName, referenceData)) {
                        setDeclinedReason("");
                        setDeclinedNotes("");
                      }
                      setOutcomeAnchor(null);
                    }}
                    width={
                      selectRef.current?.offsetWidth
                        ? `${selectRef.current.offsetWidth - 20}px`
                        : 240
                    }
                  />
                </Box>

                {/* Conditional Reason for Declined */}
                {isDeclined &&
                  String(selectedSubReason).toLowerCase() === "other" && (
                    <Box sx={{ flex: 1, minWidth: 200 }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#403C6F",
                          fontWeight: 500,
                          mb: 1,
                          display: "block",
                        }}
                      >
                        Reason{" "}
                        {!readOnly && (
                          <span style={{ color: "#D32F2F" }}>*</span>
                        )}
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={declinedReason}
                        onChange={(e) => setDeclinedReason(e.target.value)}
                        placeholder="Enter reason for declining..."
                        disabled={readOnly}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#fff",
                            "& fieldset": { borderColor: borderColor },
                          },
                        }}
                      />
                    </Box>
                  )}
              </Box>

              {/* Conditional Declined Notes */}
              {isDeclined && (
                <Box sx={{ width: "100%", mt: 1 }}>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      color: "#403C6F",
                      fontWeight: 500,
                      mb: 1,
                      display: "block",
                    }}
                  >
                    Declined Notes
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    value={declinedNotes}
                    onChange={(e) => setDeclinedNotes(e.target.value)}
                    placeholder="Enter declined notes..."
                    disabled={readOnly}
                    sx={getNotesFieldRootSx(readOnly, borderColor)}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Clinical Notes */}
          <Typography
            sx={{
              fontSize: "13px",
              color: "#403C6F",
              fontWeight: 500,
              mb: 1,
              display: "block",
            }}
          >
            Clinical Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={clinicalNotes}
            onChange={(e) => setClinicalNotes(e.target.value)}
            placeholder="Enter clinical notes..."
            disabled={readOnly}
            sx={{
              mb: 3,
              ...getNotesFieldRootSx(readOnly, borderColor),
            }}
          />

          {/* Signature Box */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: "12px",
              backgroundColor: "#FAFAFD",
              border: `1.5px solid ${borderColor}`,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <DrawIcon sx={{ color: primaryColor, fontSize: 20 }} />
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{ color: primaryColor }}
              >
                Signature
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 4, mb: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: secondaryColor, display: "block", mb: 0.5 }}
                >
                  SIGNEE NAME
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: "#10100F" }}
                >
                  {displaySigneeName}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: secondaryColor, display: "block", mb: 0.5 }}
                >
                  DATE / TIME
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: "#10100F" }}
                >
                  {currentTime}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ borderColor: borderColor, mb: 2 }} />

            <FormControlLabel
              control={
                <Checkbox
                  checked={certification}
                  onChange={(e) => setCertification(e.target.checked)}
                  disabled={readOnly}
                  sx={getCertificationCheckboxSx(
                    !readOnly && !certification,
                    borderColor,
                    primaryColor,
                  )}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{ color: "#403C6F", fontSize: "13px" }}
                >
                  I certify that the documentation provided is accurate and
                  reflects the clinical assessment performed.{" "}
                  <span style={{ color: "#D32F2F" }}>*</span>
                </Typography>
              }
            />
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{ px: 2, pb: 2, pt: 1, gap: 2, justifyContent: "flex-end" }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: "50px",
            textTransform: "none",
            fontWeight: 500,
            fontSize: "14px",
            color: primaryColor,
            borderColor: primaryColor,
            px: 4,
            py: 1,
            "&:hover": {
              backgroundColor: "rgba(64, 60, 111, 0.04)",
              borderColor: "#312e5e",
              borderWidth: "1px",
            },
            "&.Mui-disabled": {
              pointerEvents: "auto",
              cursor: "not-allowed",
            },
          }}
        >
          {readOnly ? "Close" : "Cancel"}
        </Button>
        {!readOnly && (
          <Button
            onClick={handleSubmit}
            disabled={!certification || !selectedOutcome || isSubmitting}
            variant="contained"
            sx={{
              borderRadius: "50px",
              textTransform: "none",
              fontWeight: 500,
              fontSize: "14px",
              backgroundColor: primaryColor,
              color: "#FFF",
              px: 4,
              py: 1,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#312e5e",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "rgba(64, 60, 111, 0.4)",
                color: "rgba(255, 255, 255, 0.7)",
                pointerEvents: "auto",
                cursor: "not-allowed",
              },
            }}
          >
            Submit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

BiosimilarsOutcomeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  memberId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  readOnly: PropTypes.bool,
  existingNote: PropTypes.object,
  memberData: PropTypes.object,
  assigneeRnName: PropTypes.string,
};

export default BiosimilarsOutcomeModal;
